import React, { useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  ExternalLink,
  Home,
  Lock,
  Plus,
  RotateCw,
  Search,
  Settings2,
  Shield,
  Star,
  X,
} from "lucide-react";
import { PROFILE, PROJECTS, SOCIALS } from "../data";
import "./edge.css";

const NEW_TAB = "edge://newtab";
const HOME_URL = "https://www.bing.com/";

/** Sites that ship X-Frame-Options / frame-ancestors and cannot be embedded. */
const KNOWN_BLOCKED = [
  "google.",
  "bing.com",
  "duckduckgo.com",
  "github.com",
  "linkedin.com",
  "x.com",
  "twitter.com",
  "facebook.com",
  "instagram.com",
  "youtube.com/results",
  "amazon.",
  "reddit.com",
  "stackoverflow.com",
  "medium.com",
  "netflix.com",
];

const FAVOURITES = [
  { label: "GitHub", url: `https://github.com/${PROFILE.github}` },
  { label: "LinkedIn", url: SOCIALS.find((s) => s.label === "LinkedIn")?.url },
  { label: "Resume", url: `${process.env.PUBLIC_URL || ""}/miracle_aponjolosun.pdf` },
  ...PROJECTS.slice(0, 3).map((p) => ({ label: p.title, url: p.url })),
];

const TOP_SITES = [
  { label: "Wikipedia", url: "https://www.wikipedia.org/", tone: "#3366cc" },
  { label: "MDN", url: "https://developer.mozilla.org/", tone: "#000000" },
  { label: "GitHub", url: `https://github.com/${PROFILE.github}`, tone: "#24292f" },
  { label: "YouTube", url: "https://www.youtube.com/", tone: "#ff0000" },
  { label: "Vercel", url: "https://vercel.com/", tone: "#111111" },
  { label: "Example", url: "https://example.com/", tone: "#4a6fa5" },
];

let seq = 0;
const makeTab = (url = NEW_TAB) => ({
  id: `t${++seq}`,
  history: [url],
  cursor: 0,
});

/** Turns whatever was typed into a URL, searching when it isn't one. */
function resolveInput(raw) {
  const value = raw.trim();
  if (!value) return null;
  if (/^(https?:|edge:|about:|\/)/i.test(value)) return value;
  const looksLikeHost = /^[\w-]+(\.[\w-]+)+(\/|$|\?|#)/.test(value);
  if (looksLikeHost) return `https://${value}`;
  return `https://www.bing.com/search?q=${encodeURIComponent(value)}`;
}

const hostOf = (url) => {
  try {
    return new URL(url, window.location.origin).hostname.replace(/^www\./, "");
  } catch {
    return url;
  }
};

const titleOf = (url) => {
  if (url === NEW_TAB) return "New tab";
  if (url.endsWith(".pdf")) return "Resume.pdf";
  return hostOf(url);
};

const likelyBlocked = (url) =>
  KNOWN_BLOCKED.some((needle) => url.toLowerCase().includes(needle));

/* ------------------------------------------------------------- new tab page */

function NewTab({ navigate }) {
  const [q, setQ] = useState("");
  return (
    <div className="edge-newtab">
      <div className="edge-newtab__sky" aria-hidden="true" />
      <form
        className="edge-newtab__search"
        onSubmit={(e) => {
          e.preventDefault();
          navigate(resolveInput(q));
        }}
      >
        <Search size={18} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search the web"
          aria-label="Search the web"
          autoComplete="off"
        />
      </form>

      <div className="edge-tiles">
        {TOP_SITES.map((site) => (
          <button
            key={site.label}
            className="edge-tile"
            onClick={() => navigate(site.url)}
          >
            <span className="edge-tile__icon" style={{ background: site.tone }}>
              {site.label[0]}
            </span>
            <span className="edge-tile__label">{site.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/* --------------------------------------------------------- blocked notice */

function CantReach({ url, onRetry }) {
  return (
    <div className="edge-error">
      <div className="edge-error__mark">
        <Shield size={44} strokeWidth={1.4} />
      </div>
      <h1>This site can’t be embedded</h1>
      <p className="edge-error__host">{hostOf(url)} refused to connect.</p>
      <p className="edge-error__body">
        The site sends an <code>X-Frame-Options</code> header, which tells
        browsers not to display it inside another page. That rule is enforced by
        your real browser — opening it in its own tab works normally.
      </p>
      <div className="edge-error__actions">
        <a
          className="win-btn win-btn--accent"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Open in a new tab <ExternalLink size={14} />
        </a>
        <button className="win-btn" onClick={onRetry}>
          Try again
        </button>
      </div>
      <p className="edge-error__code">ERR_BLOCKED_BY_RESPONSE</p>
    </div>
  );
}

/* ------------------------------------------------------------------ frame */

function PageFrame({ url }) {
  const [state, setState] = useState(() =>
    likelyBlocked(url) ? "blocked" : "loading"
  );
  const [nonce, setNonce] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    setState(likelyBlocked(url) ? "blocked" : "loading");
    clearTimeout(timer.current);
    if (!likelyBlocked(url)) {
      // Cross-origin refusals are silent; fall back if nothing paints in time.
      timer.current = setTimeout(() => {
        setState((s) => (s === "loading" ? "blocked" : s));
      }, 4500);
    }
    return () => clearTimeout(timer.current);
  }, [url, nonce]);

  if (state === "blocked") {
    return (
      <CantReach
        url={url}
        onRetry={() => {
          setNonce((n) => n + 1);
          setState("loading");
        }}
      />
    );
  }

  return (
    <>
      {state === "loading" && <div className="edge-progress" />}
      <iframe
        key={`${url}-${nonce}`}
        className="edge-frame"
        src={url}
        title={titleOf(url)}
        sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        referrerPolicy="no-referrer"
        onLoad={() => {
          clearTimeout(timer.current);
          setState("loaded");
        }}
      />
    </>
  );
}

/* ------------------------------------------------------------------ shell */

export default function EdgeApp({ payload }) {
  const [tabs, setTabs] = useState(() => [makeTab(payload?.url || NEW_TAB)]);
  const [activeId, setActiveId] = useState(() => tabs[0].id);
  const [draft, setDraft] = useState(payload?.url || "");
  const [typing, setTyping] = useState(false);
  const inputRef = useRef(null);

  const active = tabs.find((t) => t.id === activeId) || tabs[0];
  const url = active.history[active.cursor];

  const patch = (id, fields) =>
    setTabs((list) => list.map((t) => (t.id === id ? { ...t, ...fields } : t)));

  const navigate = (next) => {
    if (!next) return;
    const history = [...active.history.slice(0, active.cursor + 1), next];
    patch(active.id, { history, cursor: history.length - 1 });
    setDraft(next === NEW_TAB ? "" : next);
    setTyping(false);
    inputRef.current?.blur();
  };

  const addTab = () => {
    const tab = makeTab();
    setTabs((list) => [...list, tab]);
    setActiveId(tab.id);
    setDraft("");
  };

  const closeTab = (id, e) => {
    e.stopPropagation();
    setTabs((list) => {
      const next = list.filter((t) => t.id !== id);
      if (!next.length) {
        const fresh = makeTab();
        setActiveId(fresh.id);
        setDraft("");
        return [fresh];
      }
      if (id === activeId) {
        const fallback = next[next.length - 1];
        setActiveId(fallback.id);
        setDraft(fallback.history[fallback.cursor]);
      }
      return next;
    });
  };

  return (
    <div className="edge">
      <div className="edge-tabstrip">
        {tabs.map((tab) => {
          const tabUrl = tab.history[tab.cursor];
          return (
            <div
              key={tab.id}
              role="tab"
              tabIndex={0}
              aria-selected={tab.id === activeId}
              className={`edge-tab ${tab.id === activeId ? "edge-tab--on" : ""}`}
              onClick={() => {
                setActiveId(tab.id);
                setDraft(tabUrl === NEW_TAB ? "" : tabUrl);
              }}
              onKeyDown={(e) => e.key === "Enter" && setActiveId(tab.id)}
            >
              <span className="edge-tab__favicon">
                {titleOf(tabUrl)[0]?.toUpperCase()}
              </span>
              <span className="edge-tab__title">{titleOf(tabUrl)}</span>
              <button
                className="edge-tab__close"
                onClick={(e) => closeTab(tab.id, e)}
                aria-label="Close tab"
              >
                <X size={12} />
              </button>
            </div>
          );
        })}
        <button className="edge-tab__new" onClick={addTab} aria-label="New tab">
          <Plus size={15} />
        </button>
      </div>

      <div className="edge-bar">
        <button
          className="xp-icon-btn"
          disabled={active.cursor === 0}
          onClick={() => patch(active.id, { cursor: active.cursor - 1 })}
          aria-label="Back"
        >
          <ArrowLeft size={17} />
        </button>
        <button
          className="xp-icon-btn"
          disabled={active.cursor >= active.history.length - 1}
          onClick={() => patch(active.id, { cursor: active.cursor + 1 })}
          aria-label="Forward"
        >
          <ArrowRight size={17} />
        </button>
        <button
          className="xp-icon-btn"
          onClick={() => navigate(url)}
          aria-label="Refresh"
        >
          <RotateCw size={15} />
        </button>
        <button
          className="xp-icon-btn"
          onClick={() => navigate(HOME_URL)}
          aria-label="Home"
        >
          <Home size={16} />
        </button>

        <form
          className="edge-omnibox"
          onSubmit={(e) => {
            e.preventDefault();
            navigate(resolveInput(draft));
          }}
        >
          {typing ? <Search size={14} /> : <Lock size={13} />}
          <input
            ref={inputRef}
            value={typing ? draft : url === NEW_TAB ? "" : url}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={(e) => {
              setTyping(true);
              setDraft(url === NEW_TAB ? "" : url);
              e.target.select();
            }}
            onBlur={() => setTyping(false)}
            placeholder="Search or enter web address"
            aria-label="Address and search bar"
            spellCheck="false"
          />
          <Star size={15} className="edge-omnibox__icon" />
          <Settings2 size={15} className="edge-omnibox__icon" />
        </form>
      </div>

      <div className="edge-favbar">
        {FAVOURITES.filter((f) => f.url).map((fav) => (
          <button
            key={fav.label}
            className="edge-fav"
            onClick={() => navigate(fav.url)}
          >
            <span className="edge-fav__dot" />
            {fav.label}
          </button>
        ))}
      </div>

      <div className="edge-viewport">
        {url === NEW_TAB ? (
          <NewTab navigate={navigate} />
        ) : (
          <PageFrame url={url} key={url} />
        )}
      </div>
    </div>
  );
}
